/**
 * Helpers for reading LiveTrack identity off heterogeneous table rows. Rows can be
 * LiveTrack rows (id is the track id), join rows that carry live_track_id (e.g.
 * audio-file-issues), or rows with string ids — shared by the column components that
 * open per-track inspectors (lyrics, playlist usages).
 */
export function resolveLiveTrackId(data: Record<string, unknown>): number | null {
  const rawJoin = data.live_track_id;
  if (rawJoin !== undefined && rawJoin !== null && rawJoin !== '') {
    const parsed = Number(rawJoin);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  const rawId = data.id;
  if (typeof rawId === 'number' && Number.isFinite(rawId)) {
    return rawId;
  }
  if (typeof rawId === 'string' && /^\d+$/.test(rawId)) {
    return Number(rawId);
  }
  return null;
}

export function liveTrackNameFromRow(data: Record<string, unknown>): string | null {
  const name = data.name;
  return typeof name === 'string' ? name : null;
}

/** Title for the track usage inspector — single source for every entry point. */
export function trackUsageInspectorTitle(trackName: string | null | undefined): string {
  const trimmedName = trackName?.trim() ?? '';
  return trimmedName ? `Playlist Usages — ${trimmedName}` : 'Playlist Usages';
}

/** Ownership fields shared by usage rows (API resource and ES nested docs). */
export interface UsageOwnershipParts {
  account_name: string | null;
  location_name: string | null;
  location_zone_name: string | null;
}

/**
 * "{location} — {zone}" or null when neither is set. Callers decide how to render
 * the empty case (e.g. an em dash in a table cell) — no string sentinels.
 */
export function usageLocationZoneLabel(parts: UsageOwnershipParts): string | null {
  const segments = [parts.location_name, parts.location_zone_name].filter(Boolean);
  return segments.length > 0 ? segments.join(' — ') : null;
}

/**
 * "{account} / {location} — {zone}" or null when nothing is set. Single source for
 * the ownership context shown in usage drill-down subtitles and headers.
 */
export function usageOwnershipLabel(parts: UsageOwnershipParts): string | null {
  const segments = [parts.account_name, usageLocationZoneLabel(parts)].filter(Boolean);
  return segments.length > 0 ? segments.join(' / ') : null;
}

export function liveTrackArtistNameFromRow(data: Record<string, unknown>): string | null {
  const artist = data.artist;
  if (artist && typeof artist === 'object' && !Array.isArray(artist)) {
    const artistRecord = artist as Record<string, unknown>;
    if (typeof artistRecord.name === 'string') {
      return artistRecord.name;
    }
    if (typeof artistRecord.display_string === 'string') {
      return artistRecord.display_string;
    }
  }
  return null;
}
